import React from 'react';
import { X, AlertCircle } from 'lucide-react';
import type { Transaction } from './FinanceTypes';

interface TransactionFormProps {
  transaction?: Transaction;
  type: 'invoice' | 'expense';
  onSubmit: (transaction: Omit<Transaction, 'id'>) => void;
  onClose: () => void;
}

export function TransactionForm({ transaction, type, onSubmit, onClose }: TransactionFormProps) {
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const amount = Number(formData.get('amount'));
    
    if (amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    
    try {
      onSubmit({
        type,
        businessName: formData.get('businessName') as string,
        amount,
        date: formData.get('date') as string,
        status: formData.get('status') as 'paid' | 'pending' | 'overdue',
        category: formData.get('category') as string,
        description: formData.get('description') as string,
      });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-[#2A2B2E] rounded-3xl p-6 max-w-md w-full mx-4 my-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Add {type === 'invoice' ? 'Invoice' : 'Expense'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-black/20 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-xl text-red-500 mb-4">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-gray-400">Business Name</label>
            <input
              name="businessName"
              type="text"
              defaultValue={transaction?.businessName}
              required
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Amount</label>
            <input
              name="amount"
              type="number"
              defaultValue={transaction?.amount}
              step="0.01"
              required
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Date</label>
            <input
              name="date"
              type="date"
              defaultValue={transaction?.date ? new Date(transaction.date).toISOString().split('T')[0] : undefined}
              required
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Status</label>
            <select
              name="status"
              defaultValue={transaction?.status}
              required
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-400">Category</label>
            <input
              name="category"
              type="text"
              defaultValue={transaction?.category}
              required
              placeholder="e.g. Consulting, Office Supplies"
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400">Description</label>
            <textarea
              name="description"
              defaultValue={transaction?.description}
              className="w-full px-4 py-2 bg-black/20 rounded-xl mt-1 resize-none h-24"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-[#bbdde5] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
          >
            {transaction ? 'Update' : 'Add'} {type === 'invoice' ? 'Invoice' : 'Expense'}
          </button>
        </form>
      </div>
    </div>
  );
}